import IEditor from './IEditor';
import setIsDirty from '../actions/setIsDirty';
import updateContent from '../actions/updateContent';
import EditorViewState from '../store/schema/EditorViewState';
import { select as asyncSelect } from '../utils/asyncSelect';
import getDefaultComposeFormat from '../utils/getDefaultComposeFormat';
import { register as registerLogOnFrameDropWhileTyping } from '../utils/logOnFrameDropWhileTyping';
import { getPerfData, triggerEventWithMeasure } from '../utils/measurePerf';
import { PerfData } from '../utils/PerfData';
import { createPluginUtils, EditorPluginEx } from '../utils/PluginUtils';
import merge from 'lodash-es/merge';
import { logUsage } from 'owa-analytics';
import { transformElementForDarkMode } from 'owa-dark-mode-utilities';
import emojiReplacements from 'owa-expressions-store/lib/utils/emojiReplacements';
import { isFeatureEnabled } from 'owa-feature-flags';
import { format } from 'owa-strings';
import strings from 'owa-strings/owa-mail-strings';
import { isBrowserIE } from 'owa-user-agent';
import { isMac } from 'owa-user-agent/lib/userAgent';
import * as React from 'react';
import { Editor, EditorOptions, EditorPlugin, UndoService } from 'roosterjs-editor-core';
import { DefaultFormat } from 'roosterjs-editor-types';
import { AttributeCallbackMap, HtmlSanitizer } from 'roosterjs-html-sanitizer';
import {
    ContentEdit,
    CustomReplace,
    getDefaultContentEditFeatures,
    HyperLink,
    Paste,
    TableResize,
    Watermark,
} from 'roosterjs-editor-plugins';

export interface ReactEditorProps {
    viewState: EditorViewState;
    plugins?: EditorPlugin[];
    undo?: UndoService;
    useDirectPaste?: boolean;
    isRtl?: boolean;
    classNames?: string;
    updateOnKeyPress?: boolean;
    onFocus?: () => void;
    onBlur?: () => void;
    onDirectionChange?: (dirAttribute: 'ltr' | 'rtl') => void;
    showFormatPainterCursor?: boolean;
    watermark?: string;
    cssClassesToPreserveWhenPaste?: string[];
    logPerfDataCallback?: (perfData: PerfData) => void;
    ariaLabel?: string;
    isDarkMode?: boolean;
}

const FORMATPAINTERCURSOR_SVG = require('./formatpaintercursor.svg');
const styles = require('./ReactEditor.scss');
const classname = require('classnames/bind').bind(styles);
const WATERMARK_TEXT_FORMAT: DefaultFormat = {
    fontFamily: 'Segoe UI',
    fontSize: '14px',
    textColor: '#666', // $ms-color-neutralSecondary
};

export default class ReactEditor extends React.Component<ReactEditorProps, {}>
    implements IEditor<ReactEditorProps, {}> {
    private editor: Editor;
    private editorDiv: HTMLDivElement;
    private contentEditPlugin: ContentEdit;
    private hyperLinkPlugin: HyperLink;
    private pastePlugin: Paste;
    private tableResizePlugin: TableResize;
    private customReplacePlugin: CustomReplace;
    private dirAttributeMutationObserver: MutationObserver = null;
    private subtreeMutationObserver: MutationObserver = null;
    private unregisterLogOnFrameDropWhileTyping = null;

    constructor(props: ReactEditorProps) {
        super(props);

        let clickableLinkTooltip = format(
            strings.followLink,
            isMac() ? strings.ctrlClickMac : strings.ctrlClickWindows
        );
        let propertyCallbacks: AttributeCallbackMap = {
            originalsrc: this.keepValue, // For cut/paste text and image together, we need to preserve these props so that server side can handle it
            crossorigin: this.keepValue,
            contenttype: this.keepValue,
        };

        if (this.props.cssClassesToPreserveWhenPaste) {
            propertyCallbacks.class = this.cssClassCallback;
        }

        let contentEditFeatures = getDefaultContentEditFeatures();
        contentEditFeatures.smartOrderedList = true;

        this.pastePlugin = new Paste(null /*deprecated*/, propertyCallbacks);
        this.contentEditPlugin = new ContentEdit(contentEditFeatures);
        this.hyperLinkPlugin = new HyperLink(href => clickableLinkTooltip + '\r\n' + href);
        this.tableResizePlugin = new TableResize();
        this.customReplacePlugin = new CustomReplace(emojiReplacements);
    }

    componentDidMount() {
        this.editor = new Editor(this.editorDiv, this.getEditorOptions());
        this.updateContentToViewState(true /*isInitializing*/);

        // Edge on windows allows the user to directly edit the DOM of the editing
        // container if they have both an rtl and ltr language installed. Capture
        // these edits and propagate them to the consumer.
        if (this.props.onDirectionChange !== undefined) {
            this.dirAttributeMutationObserver = new MutationObserver(this.onBrowserDirectionChange);
            this.dirAttributeMutationObserver.observe(this.editorDiv, {
                attributes: true,
                attributeFilter: ['dir'],
            });
        }

        // IE11 does not fire input events on contentEditable elements, so we cannot rely on onInput
        // to mark the viewstate dirty. In this case, observe changes to the subtree to detect changes
        // in a locale-safe way (we would have to manually whitelist all locale's non-editing
        // keypresses otherwise, which is not safe against key remapping).
        const shouldObserveSubtree =
            isBrowserIE() && isFeatureEnabled('cmp-mutationObserverReactEditorIE11');
        if (shouldObserveSubtree) {
            this.subtreeMutationObserver = new MutationObserver(this.onEditorContentChanged);
            this.subtreeMutationObserver.observe(this.editorDiv, {
                attributes: true,
                subtree: true,
                childList: true,
                characterData: true,
            });
        }

        if (isFeatureEnabled('cmp-logOnFrameDropWhileTyping')) {
            this.unregisterLogOnFrameDropWhileTyping = registerLogOnFrameDropWhileTyping(
                this.editorDiv,
                'ReactEditor'
            );
        }
    }

    componentWillUnmount() {
        if (this.editor) {
            if (this.props.logPerfDataCallback) {
                let perfData = getPerfData(this.editor);
                this.props.logPerfDataCallback(perfData);
            }
            this.editor.dispose();
        }

        this.editor = null;

        if (this.dirAttributeMutationObserver !== null) {
            this.dirAttributeMutationObserver.disconnect();
            this.dirAttributeMutationObserver = null;
        }

        if (this.subtreeMutationObserver !== null) {
            this.subtreeMutationObserver.disconnect();
            this.subtreeMutationObserver = null;
        }

        if (this.unregisterLogOnFrameDropWhileTyping) {
            this.unregisterLogOnFrameDropWhileTyping();
        }
    }

    componentWillReceiveProps(nextProps: ReactEditorProps) {
        // If being provided a new view state, reset the content
        if (nextProps.viewState !== this.props.viewState) {
            this.setEditorContent(nextProps.viewState.content);
        }
        if (nextProps.isDarkMode !== this.props.isDarkMode) {
            this.setDarkMode(nextProps.isDarkMode);
        }
    }

    render() {
        let {
            onFocus,
            classNames,
            isRtl,
            showFormatPainterCursor,
            watermark,
            ariaLabel,
            isDarkMode,
        } = this.props;
        let style = showFormatPainterCursor
            ? {
                  cursor: `url("${FORMATPAINTERCURSOR_SVG}") 8.5 16, auto`,
              }
            : null;

        return (
            <div
                contentEditable
                ref={ref => (this.editorDiv = ref)}
                dir={isRtl ? 'rtl' : 'ltr'}
                className={classname(classNames, styles.textSelectable, {
                    resetColors: !isDarkMode,
                })}
                onBlur={this.onBlur}
                onFocus={onFocus}
                onInput={this.onEditorContentChanged}
                style={style}
                role="textbox"
                aria-multiline="true"
                aria-label={ariaLabel ? ariaLabel : watermark}
            />
        );
    }

    public focus() {
        if (this.editor) {
            this.editor.focus();
        }
    }

    public getContent(): string {
        return this.editor.getContent();
    }

    public updateContentToViewState(isInitializing?: boolean) {
        if (this.editor) {
            updateContent(this.props.viewState, this.editor.getContent(), isInitializing);
        }
    }

    public getContentHeight() {
        return this.editorDiv ? this.editorDiv.scrollHeight : 0;
    }

    private getEditorOptions() {
        let { plugins, viewState, undo, watermark, logPerfDataCallback, isDarkMode } = this.props;
        let allPlugins: EditorPlugin[] = plugins ? [...plugins] : [];
        let defaultFormat = getDefaultComposeFormat();

        allPlugins = allPlugins.concat([
            this.contentEditPlugin,
            this.hyperLinkPlugin,
            this.pastePlugin,
            this.tableResizePlugin,
            this.customReplacePlugin,
        ]);

        if (watermark) {
            allPlugins.push(new Watermark(watermark, WATERMARK_TEXT_FORMAT));
        }

        let pluginUtils = createPluginUtils(this.pastePlugin);
        allPlugins.forEach(plugin => {
            let pluginEx = plugin as EditorPluginEx;
            if (pluginEx.setPluginUtils) {
                pluginEx.setPluginUtils(pluginUtils);
            }
        });

        let initialContent = HtmlSanitizer.convertInlineCss(viewState.content);
        let options: EditorOptions = {
            plugins: allPlugins,
            defaultFormat: defaultFormat,
            undo: undo,
            initialContent: initialContent,
            inDarkMode: isDarkMode,
            darkModeOptions:
                isDarkMode || isFeatureEnabled('cmp-darkModeToggle')
                    ? {
                          transformOnInitialize: true,
                          onExternalContentTransform: transformElementForDarkMode,
                      }
                    : {},
            coreApiOverride: merge(
                logPerfDataCallback
                    ? {
                          triggerEvent: triggerEventWithMeasure,
                      }
                    : {},
                isFeatureEnabled('cmp-asyncSelect')
                    ? {
                          select: asyncSelect,
                      }
                    : {}
            ),
        };

        return options;
    }

    private onBlur = () => {
        this.updateContentToViewState();
        if (this.props.onBlur) {
            this.props.onBlur();
        }
    };

    private onEditorContentChanged = () => {
        let { updateOnKeyPress, viewState } = this.props;
        if (updateOnKeyPress) {
            this.updateContentToViewState();
        } else if (!viewState.isDirty) {
            setIsDirty(viewState, true /*isDirty*/);
        }
    };

    private cssClassCallback = (value: string) => {
        let values = value.split(' ');
        values = values.filter(
            value => this.props.cssClassesToPreserveWhenPaste.indexOf(value) >= 0
        );
        return values.length > 0 ? values.join(' ') : null;
    };

    private keepValue = (value: string) => value;

    private setEditorContent(content: string) {
        let contentAfterCSSInlining = HtmlSanitizer.convertInlineCss(content);
        this.editor.setContent(
            contentAfterCSSInlining,
            true /* triggerContentChangedEvent */,
            this.props.isDarkMode
        );
        this.updateContentToViewState(true /*isInitializing*/);
    }

    private setDarkMode(nextDarkMode: boolean) {
        this.editor.setDarkModeState(nextDarkMode);
    }

    private onBrowserDirectionChange = (records: MutationRecord[]) => {
        const mutationRecord = records.length !== 0 ? records[0] : null;
        if (mutationRecord === null || mutationRecord.type !== 'attributes') {
            // This listener is only registered as listening to a single attribute on a single
            // element, so it should only ever receive one MutationRecord of type attribute
            logUsage(
                'direction_change_invalid_mutation_callback',
                records.map(record => JSON.stringify(record)),
                { isCore: true }
            );
            return;
        }

        // Cast here is sane because target of an attribute mutation is the HTML element
        // for which an attribute has changed
        const dirAttribute: string = (mutationRecord.target as HTMLElement).getAttribute('dir');

        // Filter direction changes to 'rtl' and 'ltr'. We explicitly do not support the 'auto' state.
        if (!dirAttribute || dirAttribute === 'auto') {
            this.props.onDirectionChange(null);
        } else if (dirAttribute === 'ltr' || dirAttribute === 'rtl') {
            this.props.onDirectionChange(dirAttribute);
        }
    };
}
